import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PackageCheck, Plus, RefreshCw, ShoppingCart } from 'lucide-react';
import { PURCHASE_ORDER_TRANSITIONS, canTransition } from '@/lib/domain';
import { PurchaseOrderStatus, PurchaseReceiptStatus } from '@/lib/shared-types';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Icon } from '@/components/ui/icon';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { PROCUREMENT_TABS } from '@/Pages/tabRoutes';
import { apiMessage, formatDateTime, formatQuantity, useQueryParams } from './shared';
import { PurchaseOrderForm, type UnitOption } from './components/PurchaseOrderForm';
import { ReceiveStockSheet } from './components/ReceiveStockSheet';


const PO_STATUSES = Object.values(PurchaseOrderStatus);
const RECEIPT_STATUSES: PurchaseReceiptStatus[] = Object.values(PurchaseReceiptStatus);

interface Supplier {
  id: string;
  name: string;
  code: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  paymentTerms: string | null;
  isActive: boolean;
  _count?: { purchaseOrders: number; purchaseReceipts: number };
}

interface PurchaseOrderLine {
  id: string;
  inventoryItemId: string;
  quantity: string;
  receivedQuantity: string;
  unitCost: string;
  lineTotal: string;
  inventoryItem: { id: string; name: string; sku: string | null } | null;
  unit: { id: string; code: string } | null;
}

interface PurchaseOrder {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  branchId: string | null;
  currency: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  expectedAt: string | null;
  createdAt: string;
  notes: string | null;
  supplier: { id: string; name: string; code: string | null } | null;
  warehouse?: { id: string; name: string } | null;
  lines?: PurchaseOrderLine[];
  _count?: { lines: number; receipts: number };
}

interface GoodsReceipt {
  id: string;
  number: string;
  status: string;
  invoiceNumber: string | null;
  currency: string;
  total: string;
  receivedAt: string;
  supplier: { id: string; name: string } | null;
  warehouse: { id: string; name: string } | null;
  _count?: { lines: number };
}

interface InventoryItemOption {
  id: string;
  name: string;
  sku: string | null;
  unitId: string;
}

interface WarehouseOption {
  id: string;
  name: string;
}

interface PriceHistoryRow {
  id: string;
  quantity: string;
  unitCost: string;
  lineTotal: string;
  unit: { id: string; code: string } | null;
  receipt: {
    id: string;
    number: string;
    receivedAt: string;
    invoiceNumber: string | null;
    currency: string;
    supplier: { id: string; name: string } | null;
  };
}

interface PriceHistorySummary {
  purchases: number;
  minUnitCost: string | null;
  maxUnitCost: string | null;
  averageUnitCost: string | null;
  lastUnitCost: string | null;
  lastPurchasedAt: string | null;
  currentMovingAverage: string;
}

/**
 * `PurchaseOrdersService.requireReceivable()` accepts APPROVED and
 * PARTIALLY_RECEIVED — which are exactly the statuses from which the machine
 * can still reach a received one. Deriving it keeps the button and that guard
 * from drifting apart the way a copied status list would.
 */
const isReceivable = (status: PurchaseOrderStatus) =>
  canTransition(PURCHASE_ORDER_TRANSITIONS, status, PurchaseOrderStatus.PARTIALLY_RECEIVED) ||
  canTransition(PURCHASE_ORDER_TRANSITIONS, status, PurchaseOrderStatus.RECEIVED);

/**
 * `cancel()` refuses an order any of whose lines has been received — close it
 * short instead. `refreshReceiptStatus()` only ever leaves an order
 * PARTIALLY_RECEIVED or RECEIVED once a delivery has landed, so the status
 * answers that question for list rows, which carry no lines to inspect.
 */
const hasReceivedGoods = (status: PurchaseOrderStatus) =>
  status === PurchaseOrderStatus.PARTIALLY_RECEIVED ||
  status === PurchaseOrderStatus.RECEIVED;

/**
 * §22 — suppliers, purchase orders, goods receipts and purchase price history.
 *
 * The Inventory screen covers the *ledger* — what is on the shelf and what
 * moved it. Nothing covered where stock comes from, so ordering, receiving and
 * "what have we been paying for this?" had no interface at all: a manager could
 * see they were about to run out of milk and had no way to order more.
 *
 * Everything reads on `inventory.view` and every mutation on `inventory.adjust`,
 * mirroring the API exactly.
 */
export default function Procurement() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { format } = useCurrency();

  /**
   * A purchase document carries its own `currency` — a supplier abroad is
   * invoiced in their money, not the branch's. Honour the row's code when it has
   * one and fall back to the branch's only for figures that are genuinely
   * tenant-scoped (an item's moving average, say).
   */
  const money = (value: string | null | undefined, currency?: string | null) =>
    currency ? formatCurrency(value ?? 0, currency, i18n.language) : format(value);

  const routed = useRoutedTabs('/procurement', PROCUREMENT_TABS);
  const tab = routed.open ? routed.current : null;
  const [status, setStatus] = useState('');
  const [receiptStatus, setReceiptStatus] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const [detailId, setDetailId] = useState<string | null>(null);
  /** Raising an order and booking a delivery are both whole sheets rather than
   *  dialogs: each is a table of lines beside the document it belongs to. */
  const [creating, setCreating] = useState(false);
  const [supplierForm, setSupplierForm] = useState<Supplier | null>(null);
  const [receiveFor, setReceiveFor] = useState<PurchaseOrder | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [priceItemId, setPriceItemId] = useState('');

  const mayWrite = can('inventory.adjust');

  // ------------------------------------------------------------------ data

  const suppliers = useApi<unknown>('/procurement/suppliers', { limit: 200 });
  const supplierRows = useMemo(() => rowsOf<Supplier>(suppliers.data).rows, [suppliers.data]);
  // The order form only offers active suppliers, so an inactive-only list is
  // the same as no supplier at all as far as raising an order goes.
  const activeSuppliers = useMemo(() => supplierRows.filter((s) => s.isActive), [supplierRows]);

  const items = useApi<unknown>('/inventory/items', { limit: 300 });
  const itemRows = useMemo(() => rowsOf<InventoryItemOption>(items.data).rows, [items.data]);

  const units = useApi<unknown>('/inventory/units', { limit: 200 });
  const unitRows = useMemo(() => rowsOf<UnitOption>(units.data).rows, [units.data]);

  const warehouses = useApi<unknown>('/inventory/warehouses', { limit: 100 });
  const warehouseRows = useMemo(
    () => rowsOf<WarehouseOption>(warehouses.data).rows,
    [warehouses.data],
  );

  const orderParams = useQueryParams({ limit: 100, status, supplierId, search });
  const orders = useApi<unknown>(tab === 'orders' ? '/procurement/purchase-orders' : null, orderParams);
  const orderRows = useMemo(() => rowsOf<PurchaseOrder>(orders.data).rows, [orders.data]);

  const receiptParams = useQueryParams({ limit: 100, status: receiptStatus, supplierId, search });
  const receipts = useApi<unknown>(tab === 'receipts' ? '/procurement/receipts' : null, receiptParams);
  const receiptRows = useMemo(() => rowsOf<GoodsReceipt>(receipts.data).rows, [receipts.data]);

  const detail = useApi<PurchaseOrder>(
    detailId ? `/procurement/purchase-orders/${detailId}` : null,
  );
  const detailOrder = detail.data;

  const priceHistory = useApi<unknown>(
    tab === 'prices' && priceItemId ? `/procurement/price-history/${priceItemId}` : null,
    { limit: 50 },
  );
  const priceRows = useMemo(
    () => rowsOf<PriceHistoryRow>(priceHistory.data).rows,
    [priceHistory.data],
  );
  // `paginate()` is spread beside `summary`, so the envelope puts min/max/average
  // in `meta` alongside page and total rather than in the row array.
  const priceSummary =
    (priceHistory.data as { meta?: { summary?: PriceHistorySummary } } | null)?.meta?.summary ??
    null;

  const run = async (key: string, request: () => Promise<unknown>, successKey: string) => {
    setBusy(key);
    try {
      await request();
      toast.success(t(successKey));
      orders.reload();
      receipts.reload();
      suppliers.reload();
      if (detailId) detail.reload();
      return true;
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /** No supplier to name, or nothing to order, means the form cannot be filled. */
  const newOrderBlocked = !activeSuppliers.length
    ? t('platform.procurement.needsSupplier')
    : !itemRows.length
      ? t('platform.procurement.needsItems')
      : null;

  const openReceive = (order: PurchaseOrder) => {
    setDetailId(order.id);
    setReceiveFor(order);
  };

  const submitSupplier = async () => {
    if (!supplierForm?.name.trim()) return;
    const body = {
      name: supplierForm.name.trim(),
      code: supplierForm.code?.trim() || undefined,
      contactName: supplierForm.contactName?.trim() || undefined,
      phone: supplierForm.phone?.trim() || undefined,
      email: supplierForm.email?.trim() || undefined,
      paymentTerms: supplierForm.paymentTerms?.trim() || undefined,
      isActive: supplierForm.isActive,
    };
    const ok = await run(
      'supplier',
      () =>
        supplierForm.id
          ? api.patch(`/procurement/suppliers/${supplierForm.id}`, body)
          : api.post('/procurement/suppliers', body),
      'platform.procurement.supplierSaved',
    );
    if (ok) setSupplierForm(null);
  };

  // ---------------------------------------------------------------- render

  /**
   * Raising an order and booking a delivery each take the page. Both are a
   * document plus a table of lines, and the receive sheet in particular has to
   * hold every outstanding line beside the quantity that actually turned up —
   * a modal's scroll area was hiding the line the operator was correcting.
   */
  if (creating) {
    return (
      <PurchaseOrderForm
        suppliers={activeSuppliers}
        items={itemRows}
        units={unitRows}
        warehouses={warehouseRows}
        onCancel={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          orders.reload();
        }}
      />
    );
  }

  if (receiveFor) {
    return (
      <ReceiveStockSheet
        order={detailOrder ?? receiveFor}
        loading={detail.loading}
        error={detail.error}
        onRetry={detail.reload}
        warehouses={warehouseRows}
        onCancel={() => {
          setReceiveFor(null);
          setDetailId(null);
        }}
        onReceived={() => {
          setReceiveFor(null);
          setDetailId(null);
          orders.reload();
          receipts.reload();
        }}
      />
    );
  }


  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            {/* Same glyph as the nav row that reaches this page. */}
            <Icon as={ShoppingCart} size="lg" />
            {t('platform.procurement.title')}
          </h1>
          <p className="text-sm text-ink-muted">
            {t('platform.procurement.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {tab === 'orders' && mayWrite && (
            <Button
              size="sm"
              disabled={!!newOrderBlocked}
              title={newOrderBlocked ?? undefined}
              onClick={() => setCreating(true)}
            >
              <Plus className="h-4 w-4" />
              {t('platform.procurement.newOrder')}
            </Button>
          )}
          {tab === 'suppliers' && mayWrite && (
            <Button
              size="sm"
              onClick={() =>
                setSupplierForm({
                  id: '',
                  name: '',
                  code: '',
                  contactName: '',
                  phone: '',
                  email: '',
                  address: '',
                  paymentTerms: '',
                  isActive: true,
                })
              }
            >
              <Plus className="h-4 w-4" />
              {t('platform.procurement.newSupplier')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              orders.reload();
              receipts.reload();
              suppliers.reload();
            }}
          >
            <RefreshCw className="h-4 w-4" />
            {t('platform.approvals.refresh')}
          </Button>
        </div>
      </header>

      <Tabs
        tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
        value={current}
        onChange={select}
        label={t('platform.procurement.title')}
        idPrefix="procurement"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {tab !== 'prices' && (
        <Card>
          <CardContent className="pt-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tab === 'orders' && (
                <Field label={t('platform.procurement.status')}>
                  <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">{t('platform.procurement.allStatuses')}</option>
                    {PO_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`platform.procurement.poStatus.${s}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {tab === 'receipts' && (
                <Field label={t('platform.procurement.status')}>
                  <Select value={receiptStatus} onChange={(e) => setReceiptStatus(e.target.value)}>
                    <option value="">{t('platform.procurement.allStatuses')}</option>
                    {RECEIPT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`platform.procurement.receiptStatus.${s}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {tab !== 'suppliers' && (
                <Field label={t('platform.procurement.supplier')}>
                  <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                    <option value="">{t('platform.procurement.allSuppliers')}</option>
                    {supplierRows.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              <Field label={t('common.search')}>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} />
              </Field>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------------ purchase orders */}
      {tab === 'orders' &&
        (orders.loading && !orderRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : orders.error ? (
          <ErrorState message={orders.error} onRetry={orders.reload} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('platform.procurement.number')}</TH>
                  <TH>{t('platform.procurement.supplier')}</TH>
                  <TH>{t('platform.procurement.status')}</TH>
                  <TH>{t('platform.procurement.expected')}</TH>
                  <TH className="text-end">{t('platform.procurement.lines')}</TH>
                  <TH className="text-end">{t('common.total')}</TH>
                  <TH className="text-end">{t('platform.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {!orderRows.length && (
                  <TableEmpty colSpan={7}>
                    <EmptyState
                      title={t('platform.procurement.noOrders')}
                      hint={t('platform.procurement.noOrdersHint')}
                    />
                  </TableEmpty>
                )}

                {orderRows.map((order) => (
                  <TR key={order.id}>
                    <TD>
                      <button
                        type="button"
                        className="font-medium text-accent underline"
                        onClick={() => setDetailId(order.id)}
                      >
                        {order.number}
                      </button>
                    </TD>
                    <TD>{order.supplier?.name ?? '—'}</TD>
                    <TD>
                      <Badge tone={toneForStatus(order.status)}>
                        {t(`platform.procurement.poStatus.${order.status}`)}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(order.expectedAt, i18n.language)}
                    </TD>
                    <TD className="text-end tabular-nums">{order._count?.lines ?? '—'}</TD>
                    <TD className="text-end font-medium tabular-nums">
                      {money(order.total, order.currency)}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {mayWrite &&
                          canTransition(
                            PURCHASE_ORDER_TRANSITIONS,
                            order.status,
                            PurchaseOrderStatus.SUBMITTED,
                          ) && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy === `submit:${order.id}`}
                              onClick={() =>
                                void run(
                                  `submit:${order.id}`,
                                  () =>
                                    api.post(`/procurement/purchase-orders/${order.id}/submit`, {}),
                                  'platform.procurement.submitted',
                                )
                              }
                            >
                              {t('platform.procurement.submit')}
                            </Button>
                          )}

                        {mayWrite &&
                          canTransition(
                            PURCHASE_ORDER_TRANSITIONS,
                            order.status,
                            PurchaseOrderStatus.APPROVED,
                          ) && (
                            <Button
                              size="sm"
                              disabled={busy === `approve:${order.id}`}
                              onClick={() =>
                                void run(
                                  `approve:${order.id}`,
                                  () =>
                                    api.post(`/procurement/purchase-orders/${order.id}/approve`, {}),
                                  'platform.procurement.approved',
                                )
                              }
                            >
                              {t('platform.procurement.approve')}
                            </Button>
                          )}

                        {mayWrite && isReceivable(order.status) && (
                          <Button
                            size="sm"
                            onClick={() => openReceive(order)}
                          >
                            <PackageCheck className="h-4 w-4" />
                            {t('platform.procurement.receive')}
                          </Button>
                        )}

                        {mayWrite &&
                          canTransition(
                            PURCHASE_ORDER_TRANSITIONS,
                            order.status,
                            PurchaseOrderStatus.CLOSED,
                          ) && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy === `close:${order.id}`}
                              onClick={() =>
                                void run(
                                  `close:${order.id}`,
                                  () =>
                                    api.post(`/procurement/purchase-orders/${order.id}/close`, {}),
                                  'platform.procurement.closed',
                                )
                              }
                            >
                              {t('platform.procurement.close')}
                            </Button>
                          )}

                        {/* The machine permits CANCELLED from here, but an order
                            that has taken a delivery is closed short instead —
                            shown disabled so the alternative is discoverable
                            rather than the button simply vanishing. */}
                        {mayWrite &&
                          canTransition(
                            PURCHASE_ORDER_TRANSITIONS,
                            order.status,
                            PurchaseOrderStatus.CANCELLED,
                          ) && (
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={hasReceivedGoods(order.status)}
                              title={
                                hasReceivedGoods(order.status)
                                  ? t('platform.procurement.cancelBlockedReceived')
                                  : undefined
                              }
                              onClick={() => setCancelTarget(order)}
                            >
                              {t('common.cancel')}
                            </Button>
                          )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------------------ goods receipts */}
      {tab === 'receipts' &&
        (receipts.loading && !receiptRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : receipts.error ? (
          <ErrorState message={receipts.error} onRetry={receipts.reload} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('platform.procurement.number')}</TH>
                  <TH>{t('platform.procurement.supplier')}</TH>
                  <TH>{t('platform.procurement.warehouse')}</TH>
                  <TH>{t('platform.procurement.invoice')}</TH>
                  <TH>{t('platform.procurement.status')}</TH>
                  <TH>{t('platform.procurement.receivedAt')}</TH>
                  <TH className="text-end">{t('common.total')}</TH>
                </TR>
              </THead>
              <tbody>
                {!receiptRows.length && (
                  <TableEmpty colSpan={7}>
                    <EmptyState
                      title={t('platform.procurement.noReceipts')}
                      hint={t('platform.procurement.noReceiptsHint')}
                    />
                  </TableEmpty>
                )}

                {receiptRows.map((receipt) => (
                  <TR key={receipt.id}>
                    <TD className="font-medium">{receipt.number}</TD>
                    <TD>{receipt.supplier?.name ?? '—'}</TD>
                    <TD>{receipt.warehouse?.name ?? '—'}</TD>
                    <TD>{receipt.invoiceNumber ?? '—'}</TD>
                    <TD>
                      <Badge tone={toneForStatus(receipt.status)}>
                        {t(`platform.procurement.receiptStatus.${receipt.status}`)}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(receipt.receivedAt, i18n.language)}
                    </TD>
                    <TD className="text-end font-medium tabular-nums">
                      {money(receipt.total, receipt.currency)}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ---------------------------------------------------------- suppliers */}
      {tab === 'suppliers' &&
        (suppliers.loading && !supplierRows.length ? (
          <LoadingState label={t('common.loading')} />
        ) : suppliers.error ? (
          <ErrorState message={suppliers.error} onRetry={suppliers.reload} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('platform.procurement.supplier')}</TH>
                  <TH>{t('platform.procurement.code')}</TH>
                  <TH>{t('platform.procurement.contact')}</TH>
                  <TH>{t('platform.procurement.terms')}</TH>
                  <TH className="text-end">{t('platform.procurement.orderCount')}</TH>
                  <TH className="text-end">{t('platform.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {!supplierRows.length && (
                  <TableEmpty colSpan={6}>
                    <EmptyState
                      title={t('platform.procurement.noSuppliers')}
                      hint={t('platform.procurement.noSuppliersHint')}
                    />
                  </TableEmpty>
                )}

                {supplierRows.map((supplier) => (
                  <TR key={supplier.id}>
                    <TD>
                      <span className="font-medium">{supplier.name}</span>
                      {!supplier.isActive && (
                        <Badge tone="neutral" className="ms-2">
                          {t('platform.procurement.inactive')}
                        </Badge>
                      )}
                    </TD>
                    <TD>{supplier.code ?? '—'}</TD>
                    <TD>
                      {supplier.contactName ?? '—'}
                      {supplier.phone && (
                        <div className="text-xs text-ink-muted">
                          {supplier.phone}
                        </div>
                      )}
                    </TD>
                    <TD>{supplier.paymentTerms ?? '—'}</TD>
                    <TD className="text-end tabular-nums">
                      {supplier._count?.purchaseOrders ?? '—'}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1.5">
                        {mayWrite && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSupplierForm(supplier)}
                          >
                            {t('common.edit')}
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {/* ------------------------------------------------------ price history */}
      {tab === 'prices' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-5">
              <Field
                label={t('platform.procurement.priceItem')}
                hint={t('platform.procurement.priceItemHint')}
              >
                <Select value={priceItemId} onChange={(e) => setPriceItemId(e.target.value)}>
                  <option value="">{t('platform.procurement.chooseItem')}</option>
                  {itemRows.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {item.sku ? ` · ${item.sku}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            </CardContent>
          </Card>

          {!priceItemId ? (
            <EmptyState
              title={t('platform.procurement.choosePricePrompt')}
              hint={t('platform.procurement.choosePriceHint')}
            />
          ) : priceHistory.loading ? (
            <LoadingState label={t('common.loading')} />
          ) : priceHistory.error ? (
            <ErrorState message={priceHistory.error} onRetry={priceHistory.reload} />
          ) : (
            <>
              {/* These four aggregate every delivery of the item, which may have
                  been invoiced in more than one currency, and the last is the
                  tenant-scoped moving average — so they are shown in the
                  branch's currency rather than in any one document's. */}
              {priceSummary && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {(
                    [
                      ['lowest', priceSummary.minUnitCost],
                      ['average', priceSummary.averageUnitCost],
                      ['highest', priceSummary.maxUnitCost],
                      ['movingAverage', priceSummary.currentMovingAverage],
                    ] as const
                  ).map(([key, value]) => (
                    <Card key={key}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-ink-muted">
                          {t(`platform.procurement.priceStat.${key}`)}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-lg font-semibold tabular-nums">
                        {value === null ? '—' : format(value)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('platform.procurement.receivedAt')}</TH>
                      <TH>{t('platform.procurement.supplier')}</TH>
                      <TH>{t('platform.procurement.number')}</TH>
                      <TH className="text-end">{t('common.quantity')}</TH>
                      <TH className="text-end">{t('platform.procurement.unitCost')}</TH>
                      <TH className="text-end">{t('common.total')}</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {!priceRows.length && (
                      <TableEmpty colSpan={6}>
                        <EmptyState title={t('platform.procurement.noPurchases')} />
                      </TableEmpty>
                    )}
                    {priceRows.map((row) => (
                      <TR key={row.id}>
                        <TD className="whitespace-nowrap text-ink-muted">
                          {formatDateTime(row.receipt.receivedAt, i18n.language)}
                        </TD>
                        <TD>{row.receipt.supplier?.name ?? '—'}</TD>
                        <TD>{row.receipt.number}</TD>
                        <TD className="text-end tabular-nums">
                          {formatQuantity(row.quantity)} {row.unit?.code ?? ''}
                        </TD>
                        <TD className="text-end tabular-nums">{money(row.unitCost, row.receipt.currency)}</TD>
                        <TD className="text-end font-medium tabular-nums">
                          {money(row.lineTotal, row.receipt.currency)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------ dialogs */}

      <Dialog
        open={!!detailId && !receiveFor}
        onClose={() => setDetailId(null)}
        title={detailOrder ? `${t('platform.procurement.order')} ${detailOrder.number}` : '…'}
        className="max-w-3xl"
      >
        {detail.loading ? (
          <LoadingState label={t('common.loading')} />
        ) : !detailOrder ? (
          <EmptyState title={t('platform.procurement.noOrders')} />
        ) : (
          <div className="space-y-4">
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink-muted">
                  {t('platform.procurement.supplier')}
                </dt>
                <dd>{detailOrder.supplier?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">
                  {t('platform.procurement.warehouse')}
                </dt>
                <dd>{detailOrder.warehouse?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">
                  {t('platform.procurement.status')}
                </dt>
                <dd>
                  <Badge tone={toneForStatus(detailOrder.status)}>
                    {t(`platform.procurement.poStatus.${detailOrder.status}`)}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-ink-muted">
                  {t('platform.procurement.expected')}
                </dt>
                <dd>{formatDateTime(detailOrder.expectedAt, i18n.language)}</dd>
              </div>
            </dl>

            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('platform.procurement.item')}</TH>
                    <TH className="text-end">{t('platform.procurement.ordered')}</TH>
                    <TH className="text-end">{t('platform.procurement.receivedQty')}</TH>
                    <TH className="text-end">{t('platform.procurement.unitCost')}</TH>
                    <TH className="text-end">{t('common.total')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {(detailOrder.lines ?? []).map((line) => (
                    <TR key={line.id}>
                      <TD>
                        {line.inventoryItem?.name ?? line.inventoryItemId}
                        {line.inventoryItem?.sku && (
                          <div className="text-xs text-ink-muted">
                            {line.inventoryItem.sku}
                          </div>
                        )}
                      </TD>
                      <TD className="text-end tabular-nums">
                        {formatQuantity(line.quantity)} {line.unit?.code ?? ''}
                      </TD>
                      <TD className="text-end tabular-nums">
                        {formatQuantity(line.receivedQuantity)}
                      </TD>
                      <TD className="text-end tabular-nums">
                        {money(line.unitCost, detailOrder.currency)}
                      </TD>
                      <TD className="text-end font-medium tabular-nums">
                        {money(line.lineTotal, detailOrder.currency)}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            {detailOrder.notes && (
              <p className="text-sm text-ink-muted">{detailOrder.notes}</p>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!supplierForm}
        onClose={() => setSupplierForm(null)}
        title={
          supplierForm?.id
            ? t('platform.procurement.editSupplier')
            : t('platform.procurement.newSupplier')
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setSupplierForm(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void submitSupplier()} disabled={busy === 'supplier'}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {supplierForm && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('platform.procurement.supplierName')} required>
              <Input
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
              />
            </Field>
            <Field label={t('platform.procurement.code')}>
              <Input
                value={supplierForm.code ?? ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, code: e.target.value })}
              />
            </Field>
            <Field label={t('platform.procurement.contact')}>
              <Input
                value={supplierForm.contactName ?? ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, contactName: e.target.value })}
              />
            </Field>
            <Field label={t('platform.procurement.phone')}>
              <Input
                value={supplierForm.phone ?? ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
              />
            </Field>
            <Field label={t('platform.procurement.email')}>
              <Input
                type="email"
                value={supplierForm.email ?? ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
              />
            </Field>
            <Field label={t('platform.procurement.terms')}>
              <Input
                value={supplierForm.paymentTerms ?? ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, paymentTerms: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!cancelTarget}
        onClose={() => {
          setCancelTarget(null);
          setCancelReason('');
        }}
        title={t('platform.procurement.cancelTitle')}
        description={t('platform.procurement.cancelHint')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCancelTarget(null);
                setCancelReason('');
              }}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = cancelTarget;
                if (!target) return;
                void run(
                  `cancel:${target.id}`,
                  () =>
                    api.post(`/procurement/purchase-orders/${target.id}/cancel`, {
                      reason: cancelReason.trim() || undefined,
                    }),
                  'platform.procurement.cancelled',
                ).then((ok) => {
                  if (ok) {
                    setCancelTarget(null);
                    setCancelReason('');
                  }
                });
              }}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <Field label={t('platform.procurement.reason')}>
          <Textarea
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </Field>
      </Dialog>
    </div>
  );
}
